// components/communs/DemoViewer.jsx
// Utilisé par les interfaces admin/client pour prévisualiser une formation, une évaluation classique ou une évaluation enrichie.
// Délègue le rendu à LecteurFormation, LecteurEvaluation ou LecteurEvaluationRich selon le contexte, en mode demo.
import React, { useMemo } from 'react';
import LecteurFormation from './demo/LecteurFormationDemo';
import LecteurEvaluation from './demo/LecteurEvaluationDemo';
import LecteurEvaluationRich from './demo/LecteurEvaluationRichDemo'; // ⭐ Ajout

export default function DemoViewer({
    formationId,
    token,
    onClose,
    isOpen,
    userRole = 'admin',
    initialModule = null,
    initialContent = null,
    isDirectEvaluation = false,
    isEvaluationRich = false,        // ⭐ Nouvelle prop pour expliciter une évaluation enrichie
    etudiantId = null
}) {
    // Calcul dérivé avec useMemo (pas d'effet, pas de setState)
    const { mode, evaluationData, evaluationRichData } = useMemo(() => {
        if (!isOpen) {
            return { mode: null, evaluationData: null, evaluationRichData: null };
        }

        // Cas 1 : Évaluation enrichie (explicite ou détectée)
        const isRich = isEvaluationRich ||
                       (initialModule?.typeModule === 'EVALUATION_RICH') ||
                       (initialContent?.type === 'EVALUATION_RICH') ||
                       (initialContent?.isRich === true);

        if (isRich && initialModule && initialContent) {
            const evalId = initialModule.id || initialContent?.evaluationId || 'demo-eval-rich';
            const evalData = {
                id: evalId,
                titre: initialModule.titre || initialContent?.titre || 'Évaluation enrichie',
                questions: initialContent?.questions || initialContent?.data?.questions || [],
                type: initialContent?.type || 'SOMMATIVE',
                // On peut ajouter d'autres champs si nécessaire
                scoreMinimum: initialContent?.scoreMinimum || 70,
                description: initialContent?.description || ''
            };
            return { mode: 'evaluationRich', evaluationData: null, evaluationRichData: evalData };
        }

        // Cas 2 : Évaluation classique (autonome ou intégrée)
        if (initialModule && initialContent) {
            const contentType = initialContent?.type || initialModule?.type || 'UNKNOWN';
            const isEval = (contentType === 'EVALUATION' || isDirectEvaluation ||
                            initialModule?.type === 'EVALUATION' || initialModule?.typeModule === 'EVALUATION');
            if (isEval) {
                const evalId = initialModule.id || initialContent?.evaluationId || 'demo-eval';
                const evalData = {
                    id: evalId,
                    titre: initialModule.titre || initialContent?.titre || 'Évaluation',
                    questions: initialContent?.questions || initialContent?.data?.questions || [],
                    type: initialContent?.type || 'SOMMATIVE'
                };
                return { mode: 'evaluation', evaluationData: evalData, evaluationRichData: null };
            } else {
                // Module direct non évaluation : on redirige vers la formation (si formationId existe)
                // Sinon, on affiche un fallback
                return { mode: formationId ? 'formation' : null, evaluationData: null, evaluationRichData: null };
            }
        }

        // Cas 3 : Formation complète
        if (formationId) {
            return { mode: 'formation', evaluationData: null, evaluationRichData: null };
        }

        // Aucun cas reconnu
        return { mode: null, evaluationData: null, evaluationRichData: null };
    }, [isOpen, initialModule, initialContent, isDirectEvaluation, isEvaluationRich, formationId]);

    // Si la modale n'est pas ouverte, ne rien afficher
    if (!isOpen) return null;

    // Cas 1 : Évaluation enrichie
    if (mode === 'evaluationRich' && evaluationRichData) {
        return (
            <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center">
                <div className="w-full max-w-4xl h-[95vh]">
                    <LecteurEvaluationRich
                        evaluationId={evaluationRichData.id}
                        etudiantId={etudiantId}
                        onClose={onClose}
						mode="demo"
						initialEvaluationRich={evaluationRichData}
						userRole={userRole}
                        // On peut passer un flag mode="demo" si LecteurEvaluationRich le supporte
                        // Sinon, il utilisera ses propres appels API.
                        // Pour l'instant, on lui passe le token pour les appels éventuels.
                    />
                </div>
            </div>
        );
    }

    // Cas 2 : Évaluation classique
    if (mode === 'evaluation' && evaluationData) {
        return (
            <div className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center">
                <div className="w-full max-w-4xl h-[95vh]">
                    <LecteurEvaluation
                        evaluationId={evaluationData.id}
                        etudiantId={etudiantId}
                        onClose={onClose}
                        mode="demo"
                        initialEvaluation={evaluationData}
                        userRole={userRole}
                    />
                </div>
            </div>
        );
    }

    // Cas 3 : Formation complète
    if (mode === 'formation' && formationId) {
        return (
            <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center">
                <div className="w-full max-w-6xl h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
                    <LecteurFormation
                        formationId={formationId}
                        etudiantId={etudiantId}
                        onClose={onClose}
                        mode="demo"
                        userRole={userRole}
                        token={token}
                    />
                </div>
            </div>
        );
    }

    // Cas fallback : erreur
    return (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
            <div className="bg-white p-6 rounded-xl shadow-lg max-w-md text-center">
                <h3 className="text-lg font-bold text-red-600">Erreur</h3>
                <p className="text-gray-600">Impossible de déterminer le type de contenu à prévisualiser.</p>
                <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-700 text-white rounded-lg">
                    Fermer
                </button>
            </div>
        </div>
    );
}